'use client'

import { format } from 'date-fns'
import { ArrowUpRightIcon, CheckIcon, CopyIcon, PlusIcon, Trash2Icon } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { useState } from 'react'

import { DestructiveConfirmDialog } from '@/components/dialog/settings/destructive-confirm-dialog'
import { SettingsCard, SettingsCardHeader } from '@/components/dialog/settings/settings-card'
import {
  SETTINGS_ITEM_ATTR,
  SETTINGS_LIST_CLASS,
  SETTINGS_LIST_ITEM_CLASS,
  SETTINGS_PANEL_CLASS,
} from '@/components/dialog/settings/settings-card-styles'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { env } from '@/env'
import { PlanGatedButton } from '@/features/billing/components/plan-gated-button'
import CreateApiKeyDialog from '@/features/settings/api-keys/create-api-key-dialog'
import {
  API_KEY_CAP,
  apiKeyStatus,
  ApiKeyStatus,
  liveApiKeyCount,
  nowSeconds,
  secondsToDate,
} from '@/features/settings/api-keys/lib/api-keys'
import { useCapabilities } from '@/hooks/use-capabilities'
import { useUpgradeHint } from '@/hooks/use-upgrade-hint'
import { BillingFeature, hasFeature } from '@/lib/billing/capabilities'
import { copyText } from '@/lib/clipboard'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { getDateFnsLocale } from '@/lib/utils/date-locale'
import { useListApiKeys, useRevokeApiKey } from '@/service/api-key.hook'
import { useGet2FABackupCodes } from '@/service/user-profile.hook'
import { ApiKeyInfo } from '@/types/generated/public_api'

// The data API's reference lives on the same host the app talks to, so staging links to
// staging's docs.
const API_DOCS_URL = `${env.NEXT_PUBLIC_API_URL}/v1/docs`

export default function ApiKeysPanel() {
  const t = useTranslations('settings')
  const tBilling = useTranslations('billing')
  // Minting a key is a PRO feature (the backend refuses the mint). Keys minted
  // earlier keep working, so listing and revoking stay open on every plan.
  const { hasFeature: hasPlanFeature } = useCapabilities()
  const upgradeHint = useUpgradeHint()
  const mintLocked = !hasPlanFeature(BillingFeature.DataApi)
  const { data, isLoading, isError, refetch, isFetching } = useListApiKeys()
  const { data: backupCodes, isLoading: is2faLoading } = useGet2FABackupCodes(
    {},
    { treatMissingAsDisabled: true }
  )
  const is2faEnabled = (backupCodes?.backupCodes.length ?? 0) > 0

  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyInfo | null>(null)

  // Revoked keys are dead for good, so they aren't listed. The API has no filter for them.
  const keys = (data?.keys ?? []).filter((key) => apiKeyStatus(key) !== 'revoked')
  const now = nowSeconds()
  const liveCount = liveApiKeyCount(keys, now)
  const atCap = liveCount >= API_KEY_CAP

  const { mutateAsync: revokeApiKey, isPending: isRevoking } = useRevokeApiKey({
    onSuccess: () => {
      toast.success(t('apiKeys.revoke.success'))
      setRevokeTarget(null)
    },
    onError: (error) => {
      // success: false is a refusal, never a revoke — show the server's reason.
      toast.error(error.message || t('apiKeys.revoke.failed'))
      setRevokeTarget(null)
    },
  })

  return (
    <div className={SETTINGS_PANEL_CLASS}>
      <SettingsCard>
        <div className="flex w-full items-start justify-between gap-4">
          <div className="flex flex-col gap-2">
            <SettingsCardHeader title={t('apiKeys.title')} description={t('apiKeys.description')} />
            <a
              href={API_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-ox-cyan flex w-fit items-center gap-1 text-sm tracking-tight hover:underline"
            >
              {t('apiKeys.docsLink')}
              <ArrowUpRightIcon className="size-3.5" />
            </a>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <div className="flex items-center gap-2">
              {data && (
                <span className="text-foreground/60 text-xs tabular-nums">
                  {liveCount}/{API_KEY_CAP}
                </span>
              )}
              <PlanGatedButton
                locked={mintLocked}
                feature={tBilling('features.dataApi')}
                plan={upgradeHint((caps) => hasFeature(caps, BillingFeature.DataApi))}
                variant="secondary"
                size="sm"
                // Until the list loads we can't show the count, so don't offer create yet.
                disabled={!data || atCap}
                onClick={() => setIsCreateOpen(true)}
              >
                <PlusIcon className="size-3.5" />
                {t('apiKeys.createButton')}
              </PlanGatedButton>
            </div>
            {atCap && (
              <p className="text-foreground/60 text-xs tracking-tight">{t('apiKeys.capHint')}</p>
            )}
          </div>
        </div>

        {isLoading ? (
          <ApiKeyListSkeleton />
        ) : isError ? (
          // A failed list is never "no keys".
          <div className="flex items-center justify-between gap-4">
            <p className="text-destructive text-sm tracking-tight">{t('apiKeys.loadFailed')}</p>
            <Button variant="secondary" size="sm" disabled={isFetching} onClick={() => refetch()}>
              {t('apiKeys.retry')}
            </Button>
          </div>
        ) : keys.length === 0 ? (
          <p className="text-foreground/60 text-sm tracking-tight">{t('apiKeys.empty')}</p>
        ) : (
          <div className={SETTINGS_LIST_CLASS}>
            {keys.map((key) => (
              <ApiKeyRow
                key={key.keyId}
                apiKey={key}
                status={apiKeyStatus(key, now)}
                isRevoking={isRevoking}
                onRevoke={() => setRevokeTarget(key)}
              />
            ))}
          </div>
        )}
      </SettingsCard>

      <CreateApiKeyDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        is2faEnabled={is2faEnabled}
        is2faLoading={is2faLoading && !backupCodes}
      />

      <DestructiveConfirmDialog
        open={Boolean(revokeTarget)}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
        title={t('apiKeys.revoke.title', { label: revokeTarget?.label ?? '' })}
        description={t('apiKeys.revoke.description')}
        cancelLabel={t('cancel')}
        confirmLabel={t('apiKeys.revoke.confirm')}
        pendingLabel={t('apiKeys.revoke.pending')}
        isPending={isRevoking}
        onConfirm={() => revokeTarget && revokeApiKey({ keyId: revokeTarget.keyId })}
      />
    </div>
  )
}

const STATUS_CLASS: Record<ApiKeyStatus, string> = {
  active: 'text-chart-mint',
  halted: 'text-ox-gold',
  expired: 'text-foreground/50',
  revoked: 'text-foreground/50',
}

const ApiKeyRow = ({
  apiKey,
  status,
  isRevoking,
  onRevoke,
}: {
  apiKey: ApiKeyInfo
  status: ApiKeyStatus
  isRevoking: boolean
  onRevoke: () => void
}) => {
  const t = useTranslations('settings')
  const locale = useLocale()
  const dateLocale = getDateFnsLocale(locale)
  const formatDate = (seconds: string | undefined) => {
    const date = secondsToDate(seconds)
    return date ? format(date, 'PP', { locale: dateLocale }) : null
  }
  const isExpired = status === 'expired'
  const createdAt = formatDate(apiKey.createdAt)
  const expiresAt = formatDate(apiKey.expiresAt)

  return (
    <div
      {...SETTINGS_ITEM_ATTR}
      className={cn(SETTINGS_LIST_ITEM_CLASS, isExpired && 'opacity-60')}
    >
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-foreground truncate text-sm tracking-tight">{apiKey.label}</p>
          <Badge variant="outline" className={cn('text-xs', STATUS_CLASS[status])}>
            {t(`apiKeys.status.${status}`)}
          </Badge>
        </div>
        <KeyIdCopy keyId={apiKey.keyId} />
        <div className="flex flex-wrap items-center gap-1.5">
          {apiKey.scopes.length === 0 ? (
            <Badge variant="outline" className="text-foreground/60 text-xs font-normal">
              {t('apiKeys.scopes.none')}
            </Badge>
          ) : (
            apiKey.scopes.map((scope) => (
              <Badge
                key={scope}
                variant="outline"
                className="text-foreground/80 text-xs font-normal"
              >
                {t.has(`apiKeys.scopes.${scope}`) ? t(`apiKeys.scopes.${scope}`) : scope}
              </Badge>
            ))
          )}
        </div>
        <p className="text-foreground/60 text-xs tracking-tight">
          {createdAt && t('apiKeys.created', { date: createdAt })}
          {' — '}
          {expiresAt
            ? t(status === 'expired' ? 'apiKeys.expired' : 'apiKeys.expires', { date: expiresAt })
            : t('apiKeys.neverExpires')}
        </p>
        {status === 'halted' && (
          <p className="text-ox-gold text-xs tracking-tight">{t('apiKeys.haltedHint')}</p>
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        aria-label={t('apiKeys.revoke.confirm')}
        className="hover:bg-foreground/5 shrink-0 bg-transparent"
        disabled={isRevoking}
        onClick={onRevoke}
      >
        <Trash2Icon className="text-destructive size-4" />
      </Button>
    </div>
  )
}

/** The key's public id, shown and copied in full. It names the key (the middle of
 *  oxk_<key_id>_<secret>); the secret itself is shown once at creation and never again. */
const KeyIdCopy = ({ keyId }: { keyId: string }) => {
  const t = useTranslations('settings')
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    if (!(await copyText(keyId))) {
      toast.error(t('apiKeys.copyFailed'))
      return
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="text-foreground/50 shrink-0 text-xs tracking-tight">
        {t('apiKeys.keyId')}
      </span>
      <code className="text-foreground/80 min-w-0 font-mono text-xs break-all select-all">
        {keyId}
      </code>
      <button
        type="button"
        aria-label={t('apiKeys.copyKeyId')}
        className="text-foreground/60 hover:bg-foreground/5 hover:text-foreground shrink-0 cursor-pointer p-1 transition-colors"
        onClick={copy}
      >
        {copied ? <CheckIcon className="size-3.5" /> : <CopyIcon className="size-3.5" />}
      </button>
    </div>
  )
}

const ApiKeyListSkeleton = () => (
  <div className={SETTINGS_LIST_CLASS}>
    {Array.from({ length: 2 }).map((_, index) => (
      <div key={index} className={SETTINGS_LIST_ITEM_CLASS}>
        <div className="flex flex-col gap-2">
          <Skeleton className="bg-foreground/10 h-4 w-36" />
          <Skeleton className="bg-foreground/10 h-3 w-48" />
          <Skeleton className="bg-foreground/10 h-3 w-28" />
        </div>
        <Skeleton className="bg-foreground/10 size-8 rounded-full" />
      </div>
    ))}
  </div>
)
